import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { useConfirmationModal } from "../app/confirmation";
import { RoleModel } from "../../api/roles/RoleModel";
import { RoleAdapter } from "../../api/roles/RoleAdapter";
import type { PermissionsCatalog, Role } from "../../api/roles/Role.typed";

interface RolesContextType {
  permissionsCatalog: PermissionsCatalog | undefined;
  loadPermissionsCatalog: () => Promise<void>;
  getRole: (roleId: number) => Promise<Role>;
  createRole: (payload: {
    name: string;
    permission_list: string[];
    company_id: number;
    is_employee?: boolean;
  }) => Promise<void>;
  updateRole: (
    roleId: number,
    payload: Partial<{ name: string; permission_list: string[]; is_employee: boolean }>,
  ) => Promise<void>;
  deleteRole: (roleId: number) => Promise<void>;
}

const RolesContext = React.createContext<RolesContextType>(null!);

export function RolesProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const [permissionsCatalog, setPermissionsCatalog] = React.useState<
    PermissionsCatalog | undefined
  >();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    return new RoleAdapter(new RoleModel(appConfig));
  }, [appConfig]);

  const loadPermissionsCatalog = React.useCallback(async (): Promise<void> => {
    if (!adapter) return;
    try {
      const catalog = await adapter.getPermissionsCatalog();
      const validPerms = new Set(catalog.allPermissions || []);
      const roles = (catalog.roles || []).map((r) => ({
        ...r,
        permission_list: (r.permission_list || []).filter((p) => validPerms.has(p)),
      }));
      setPermissionsCatalog({ ...catalog, roles });
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Impossible de charger les rôles", "danger");
    }
  }, [adapter, showAlert]);

  const getRole = React.useCallback(
    (roleId: number) =>
      adapter ? adapter.getRole(roleId) : Promise.reject(new Error("No adapter")),
    [adapter],
  );

  const createRole = React.useCallback(
    async (payload: {
      name: string;
      permission_list: string[];
      company_id: number;
      is_employee?: boolean;
    }): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createRole(payload);
        await loadPermissionsCatalog();
        showAlert("Rôle créé !", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, loadPermissionsCatalog, showAlert],
  );

  const updateRole = React.useCallback(
    async (
      roleId: number,
      payload: Partial<{ name: string; permission_list: string[]; is_employee: boolean }>,
    ): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateRole(roleId, payload);
        await loadPermissionsCatalog();
        showAlert("Rôle mis à jour !", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, loadPermissionsCatalog, showAlert],
  );

  const deleteRole = React.useCallback(
    async (roleId: number): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le rôle",
        "Êtes-vous sûr de vouloir supprimer ce rôle ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteRole(roleId);
        await loadPermissionsCatalog();
        showAlert("Rôle supprimé !", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, loadPermissionsCatalog, showAlert, showConfirmation],
  );

  const value: RolesContextType = React.useMemo(
    () => ({
      permissionsCatalog,
      loadPermissionsCatalog,
      getRole,
      createRole,
      updateRole,
      deleteRole,
    }),
    [permissionsCatalog, loadPermissionsCatalog, getRole, createRole, updateRole, deleteRole],
  );

  return <RolesContext.Provider value={value}>{props.children}</RolesContext.Provider>;
}

export function useRoles() {
  const ctx = React.useContext(RolesContext);
  if (!ctx) throw new Error("useRoles must be used within RolesProvider");
  return ctx;
}
